import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowRight, BookOpen, Clock3 } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Seo } from '@/components/seo/Seo';
import { INDEX_ROBOTS } from '@/lib/seo';
import { guideEntries, guides, type GuideSlug } from '@/content/guides/guideCatalog';
import NotFound from './NotFound';
import './GuideArticle.css';

function GuideLink({ slug, children, className = '' }: { slug: GuideSlug; children: ReactNode; className?: string }) {
  return <Link to={`/guides/${slug}`} className={className}>{children}</Link>;
}

export default function GuideArticle() {
  const { slug } = useParams<{ slug: string }>();
  if (!slug || !(slug in guides)) return <NotFound />;
  const guideSlug = slug as GuideSlug;
  const guide = guides[guideSlug];
  const path = `/guides/${guideSlug}`;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Seo title={`${guide.title} | Applyt`} description={guide.description} canonical={path} robots={INDEX_ROBOTS} ogType="article" />
      <Header />
      <main id="main-content">
        <div className="border-b border-border bg-gradient-to-b from-primary/10 via-background to-background">
          <div className="mx-auto max-w-5xl px-5 pb-12 pt-10 sm:px-8 sm:pb-16 sm:pt-16">
            <nav aria-label="Breadcrumb" className="mb-8 text-sm text-muted-foreground">
              <ol className="flex flex-wrap items-center gap-2">
                <li><Link to="/" className="underline-offset-4 hover:text-foreground hover:underline">Home</Link></li>
                <li aria-hidden="true">/</li>
                <li>Guides</li>
                <li aria-hidden="true">/</li>
                <li aria-current="page" className="text-foreground">{guide.eyebrow}</li>
              </ol>
            </nav>
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-primary">{guide.eyebrow}</p>
            <h1 className="max-w-4xl text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">{guide.title}</h1>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground sm:text-xl">{guide.summary}</p>
            <p className="mt-7 flex items-center gap-2 text-sm text-muted-foreground"><Clock3 aria-hidden="true" className="h-4 w-4" /> About {guide.minutes} min read</p>
          </div>
        </div>

        <div className="mx-auto grid max-w-5xl gap-12 px-5 py-12 sm:px-8 sm:py-16 lg:grid-cols-[minmax(0,1fr)_15rem] lg:gap-16">
          <article className="min-w-0" aria-label={guide.title}>
            <div className="guide-article">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ children }) => <div className="my-8 overflow-x-auto rounded-xl border border-border" role="region" aria-label="Guide comparison table" tabIndex={0}><table className="m-0 min-w-[36rem] w-full border-collapse text-sm">{children}</table></div>,
                  th: ({ children }) => <th scope="col" className="border-b border-border bg-muted/60 px-4 py-3 text-left font-semibold">{children}</th>,
                  td: ({ children }) => <td className="border-b border-border px-4 py-3 align-top">{children}</td>,
                  a: ({ href, children }) => href?.startsWith('/') ? <Link to={href}>{children}</Link> : <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
                }}
              >{guide.content}</ReactMarkdown>
            </div>

            <section aria-labelledby="guide-next-step" className="mt-14 rounded-2xl border border-primary/20 bg-primary/5 p-6 sm:p-8">
              <p className="mb-2 text-sm font-semibold uppercase tracking-[0.12em] text-primary">Apply the method</p>
              <h2 id="guide-next-step" className="text-2xl font-semibold">Make this useful for your situation</h2>
              <p className="mt-3 leading-7 text-muted-foreground">{guide.lensContext} Review the resulting plan with the people responsible for the work.</p>
              <Link to={`/lens/${guide.lensId}`} className="mt-6 inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 py-3 font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                Explore the {guide.lensName} Lens <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </Link>
            </section>
          </article>

          <aside aria-label="More guides" className="lg:pt-2">
            <div className="rounded-2xl border border-border bg-card p-5 lg:sticky lg:top-24">
              <div className="mb-4 flex items-center gap-2 font-semibold"><BookOpen aria-hidden="true" className="h-5 w-5 text-primary" /> More guides</div>
              <ul className="space-y-4">
                {guideEntries.filter(([otherSlug]) => otherSlug !== guideSlug).map(([otherSlug, other]) => (
                  <li key={otherSlug}>
                    <GuideLink slug={otherSlug} className="group block rounded-lg p-2 -m-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{other.eyebrow}</span>
                      <span className="mt-1 block font-medium leading-snug group-hover:text-primary">{other.title}</span>
                    </GuideLink>
                  </li>
                ))}
              </ul>
              <Link to="/lenses" className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline">Browse all Expert Lenses <ArrowRight aria-hidden="true" className="h-4 w-4" /></Link>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}
